import React, { useEffect, useRef, useState } from "react";
import { HandCheck, Scribble } from "../brand/Doodles";

const STEPS = [
  {
    n: "01",
    title: "Imagine",
    body:
      "Tell me what you want to accomplish. You don't need a technical specification. Start with the problem, the idea, or the thing you wish existed.",
  },
  {
    n: "02",
    title: "Design",
    body:
      "We figure out what it actually needs to do. I turn the idea into workflows, features, integrations, and a practical plan for building it.",
  },
  {
    n: "03",
    title: "Build",
    body:
      "AI becomes the development accelerator. I use modern AI development tools to move from concept to working software dramatically faster than traditional development.",
  },
  {
    n: "04",
    title: "Test",
    body:
      "Real workflows. Real edge cases. Real humans. Something isn't finished just because the code runs. It has to work where it actually matters.",
  },
  {
    n: "05",
    title: "Ship",
    body:
      "Your idea becomes something people can use. Launch it. Learn from it. Improve it.",
  },
];

const Step = ({ s, i, inView }) => (
  <li
    className="flex gap-5 py-5"
    style={{
      borderBottom: "1px dashed rgba(11,60,220,0.22)",
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0)" : "translateX(-12px)",
      transition: `opacity 600ms var(--ggm-ease-out) ${i * 120}ms, transform 600ms var(--ggm-ease-out) ${i * 120}ms`,
    }}
    data-testid={`how-i-build-step-${s.n}`}
  >
    <span className="pt-1" style={{ color: "var(--ggm-navy)" }}><HandCheck /></span>
    <div>
      <h3 className="font-condensed font-bold uppercase tracking-wide text-xl" style={{ color: "var(--ggm-navy)" }}>
        <span className="ggm-hand mr-2 normal-case">{s.n}.</span>
        {s.title}
      </h3>
      <p className="mt-1.5 font-body text-[15px] md:text-base leading-relaxed" style={{ color: "rgba(11,30,58,0.78)" }}>
        {s.body}
      </p>
    </div>
  </li>
);

export const HowIBuild = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setInView(true), { rootMargin: "-15% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="how-i-build"
      data-testid="how-i-build-section"
      className="relative ggm-paper"
      style={{ paddingTop: "clamp(64px, 10vh, 140px)", paddingBottom: "clamp(64px, 10vh, 140px)" }}
      ref={ref}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-10 lg:grid-cols-[5fr_7fr]">
        <div className="lg:sticky lg:top-28 self-start">
          <div className="ggm-hand text-lg mb-3" data-testid="how-i-build-eyebrow" style={{ transform: "rotate(-2deg)", display: "inline-block" }}>
            How I build
          </div>
          <h2
            className="font-condensed font-bold uppercase"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.25rem)", lineHeight: 1.02, letterSpacing: "-0.02em", color: "var(--ggm-navy)" }}
            data-testid="how-i-build-headline"
          >
            From Idea to Working Product.
          </h2>
          <p
            className="relative inline-block mt-8 font-condensed font-bold uppercase"
            style={{ fontSize: "clamp(1.25rem, 2.6vw, 1.75rem)", lineHeight: 1.1, color: "var(--ggm-orbit-blue)" }}
            data-testid="how-i-build-closer"
          >
            You Imagine It. I Build It.
            <Scribble color="var(--ggm-orbit-blue)" className="absolute left-0 -bottom-3 w-full h-4" />
          </p>
        </div>

        <div
          className="relative ggm-ruled rounded-sm px-5 md:px-8 py-3 md:-rotate-[0.4deg]"
          style={{
            background: "#FFFFFF",
            boxShadow: "0 18px 40px -22px rgba(11,30,58,0.5)",
            borderLeft: "3px solid rgba(11,60,220,0.35)",
          }}
          data-testid="how-i-build-desktop"
        >
          <ol data-testid="how-i-build-mobile">
            {STEPS.map((s, i) => <Step key={s.n} s={s} i={i} inView={inView} />)}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default HowIBuild;
